import * as React from 'react';
import { Dispatch } from 'react-redux';
import { I18n, Translate, Localize } from 'react-redux-i18n';
import * as copy from 'copy-to-clipboard';

import { StoreState } from '../../../reducers';
import HeaderDropdown, { HeaderDropdownOption } from '../Header/Dropdown';
import { PublishDate } from './styles';
import { NewStyleColor } from '../../../utils/styles-constants';
import { updateOriginal, publish, getPublishedPublicationUrl, getPreprodPublicationUrl, openPreview } from '../../../containers/Editor/actions';
import { initiateShare, sharePublicationViaCopy } from '../../../containers/Share/actions';
import { createNotification } from '../../../containers/NotificationBar/actions';
import { Api } from '../../../typings/api';
import DriveItemUtils from '../../../utils/driveItem';
import Popup, { PopupTheme } from '../../Popup';
import { PopupBodyWrapper, Message } from '../CombinePanel/styles';
import { getPublishPrice } from '../../../containers/Editor/actions/sources';

interface ButtonPublishProps {
	projectUrl: string;
	urlWithPin: string;
	clientId: number;
	originalGId: number;
	tempGId: number;
	label: string;
	isDisabled: boolean;
	driveItem: Api.DriveItem;
	isShareByCopyEnabled: boolean;
	isShareByEmailEnabled: boolean;
	lastUpdateDate: string | number | null;
	isUpdating: boolean;
	dispatch: Dispatch<StoreState>;
	isPublishDue: boolean;
	actionCredits: Api.CustomActionCredits;
}

const getButtonLabel = (isProposal: boolean): string => (
	isProposal ? 'messages.send' : 'messages.publish'
);

const getDropdownTitle = (lastUpdateDate: string | number | null, isProposal: boolean, isPublished?: boolean): React.ReactNode => {
	if (isProposal) {
		return null;
	}

	if (!isPublished) {
		return <Translate value="messages.publish" />;
	}

	return (
		<>
			<Translate value="messages.published" />
			{':'}
			<PublishDate>
				{lastUpdateDate &&
					<Localize
						value={lastUpdateDate}
						dateFormat="date_format.long"
					/>
				}
			</PublishDate>
		</>
	);
};

const extraPaidActionsModalMessage = (credits, message) => Object.keys(credits).map((key, index) => {
	if (!credits[key]) {
		return null;
	}

	const action = I18n.t(`messages.${key}`);

	return (
		<Message key={index}>
			<div>
				<Translate
					key={index}
					value={`messages.${message}`}
					// @ts-ignore
					dangerousHTML={true}
					credits={credits[key]}
					action={action}
				/>
			</div>
		</Message>
	);
});

const ButtonPublish = ({
	projectUrl,
	urlWithPin,
	driveItem,
	driveItem: { published, format, id },
	lastUpdateDate,
	clientId,
	originalGId,
	tempGId,
	label,
	isShareByCopyEnabled,
	isShareByEmailEnabled,
	isUpdating,
	isDisabled,
	dispatch,
	isPublishDue,
	actionCredits,
}: ButtonPublishProps) => {
	const isProposal = DriveItemUtils.isProposal(format) && !DriveItemUtils.isMasterProposal(format);
	const [isPaymentConfirmationDisplayed, setIsPaymentConfirmationDisplayed] = React.useState(false);

	const handleOpenPublishedClick = () => {
		dispatch(openPreview(id));
	};

	const handleUpdateClick = () => {
		dispatch(updateOriginal(tempGId, originalGId));
	};

	const handlePublishClick = () => {
		if (isPublishDue) {
			dispatch(getPublishPrice(id))
				.then(() => {
					setIsPaymentConfirmationDisplayed(true);
				});
		} else {
			dispatch(publish(originalGId));
		}
	};

	const handleGetUrlClick = () => {
		if (isProposal) {
			dispatch(sharePublicationViaCopy(clientId, originalGId, label, format, PopupTheme.DARK));
			return;
		}

		const url = published ? projectUrl : urlWithPin;

		if (url) {
			copy(url);
			dispatch(createNotification('info.url_copied'));
		} else {
			dispatch(createNotification('error.get_publication_url'));

			if (published) {
				dispatch(getPublishedPublicationUrl(originalGId));
			} else {
				dispatch(getPreprodPublicationUrl(clientId, tempGId, label, true));
			}
		}
	};

	const handleSendByEmailClick = () => {
		dispatch(initiateShare([driveItem], true, Api.ShareNetwork.Uprez));
	};

	const options: HeaderDropdownOption[] = [];

	if (!isProposal) {
		if (published) {
			options.push({
				label: isUpdating ? 'messages.updating' : 'messages.update_published',
				icon: {
					name: 'iconset_uprez_update-2',
					width: '26px',
					height: '26px',
					isRotating: isUpdating,
				},
				onClick: handleUpdateClick,
			});
			options.push({
				label: 'messages.view_published',
				icon: {
					name: 'iconset_uprez_eye',
					width: '23px',
					height: '16px',
				},
				onClick: handleOpenPublishedClick,
			});
		} else {
			options.push({
				label: isUpdating ? 'messages.publishing' : 'messages.publish',
				icon: {
					name: 'iconset_uprez_rocket',
					width: '22px',
					height: '22px',
				},
				onClick: handlePublishClick,
			});
		}
	}

	if (isShareByCopyEnabled) {
		options.push({
			label: 'messages.get_url',
			icon: {
				name: 'iconset_uprez_link2',
				width: '23px',
				height: '23px',
			},
			onClick: handleGetUrlClick,
		});
	}

	if (isShareByEmailEnabled) {
		options.push({
			label: 'messages.send_by_email',
			icon: {
				name: 'iconset_uprez_send2',
				width: '23px',
				height: '23px',
			},
			onClick: handleSendByEmailClick,
		});
	}

	const message = extraPaidActionsModalMessage(actionCredits, 'notification_credits_multiline');

	return (
		<>
			<HeaderDropdown
				buttonLabel={getButtonLabel(isProposal)}
				dropdownTitle={getDropdownTitle(lastUpdateDate, isProposal, published)}
				color={NewStyleColor.Blue350}
				isDisabled={isDisabled}
				options={options}
			/>
			<Popup
				isDisplayed={isPaymentConfirmationDisplayed}
				label={I18n.t('messages.publish_publication')}
				onConfirm={() => {
					setIsPaymentConfirmationDisplayed(false);
					dispatch(publish(originalGId));
				}}
				onCancel={() => setIsPaymentConfirmationDisplayed(false)}
				actionButtonColor={NewStyleColor.Blue450}
				theme={PopupTheme.DARK}
			>
				<PopupBodyWrapper>
					{message}
				</PopupBodyWrapper>
			</Popup>
		</>
	);
};

export default ButtonPublish;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/Editor/ButtonPublish/index.tsx